import { useMemo, useState } from 'react';
import {
  Card,
  Table,
  Typography,
  Tag,
  Row,
  Col,
  Statistic,
  Space,
  Alert
} from 'antd';
import {
  WarningOutlined,
  CheckCircleOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../lib/api';
import { EquipmentCategoryDisplay, CategoryTagCloudFilter, PageContainer, PageHeader } from '../components/common';
import { hasManufacturerHealthParameters } from './utils/healthParameterVisibility';

interface EquipmentCategoryInfo {
  id: string;
  categoryLabel: string;
  color?: string | null;
  icon?: string | null;
}

interface EquipmentLifespanData {
  id: string;
  equipmentDisplayId: string;
  equipmentName: string;
  categoryId: string | null;
  categoryInfo: EquipmentCategoryInfo | null;
  manufacturerLifespan: number; // メーカー耐用年数（年）
  ageInYears: number; // 経過年数（年）
  isExpired: boolean; // 耐用年数超過フラグ
  installationDate: string | Date | null; // 稼働開始日
}

type EquipmentSummary = {
  id: string;
  displayId?: string | null;
  name: string;
  equipmentCategoryId?: string | null;
  categoryInfo?: EquipmentCategoryInfo | null;
  manufacturerLifespan?: string | number | null;
  commissionedDate?: string | Date | null;
  installationDate?: string | Date | null;
  startDate?: string | Date | null;
};

type RiskGroupEntry = EquipmentSummary | { equipment: EquipmentSummary };

type RiskGroupsResponse = {
  critical: RiskGroupEntry[];
  high: RiskGroupEntry[];
  medium: RiskGroupEntry[];
  low: RiskGroupEntry[];
};

const toPositiveNumber = (value: string | number | null | undefined): number | null => {
  if (value === null || value === undefined) {
    return null;
  }
  const numeric = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) {
    return null;
  }
  return numeric;
};

const calculateAge = (date: string | Date | null | undefined): number | null => {
  if (!date) return null;
  const installDate = new Date(date);
  if (Number.isNaN(installDate.getTime())) {
    return null;
  }
  const now = new Date();
  return (now.getTime() - installDate.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
};

export function ManufacturerLifespanPage() {
  const navigate = useNavigate();
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(undefined);
  const [selectedStatus, setSelectedStatus] = useState<string>('all'); // all, expired, active
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // カテゴリ一覧を取得
  const { data: categoriesData } = trpc.equipmentCategory.list.useQuery({
    includeInactive: false,
    sortBy: 'display_order',
    sortOrder: 'asc',
  });

  // APIから設備データを取得
  const { data: riskGroupsResponse, isLoading, error } = trpc.deterioration.getByRiskLevel.useQuery({
    onlyWithPredictions: false // 全設備を取得
  });
  const riskGroups = riskGroupsResponse as RiskGroupsResponse | undefined;

  // 設備データを変換
  const equipmentData: EquipmentLifespanData[] = useMemo(() => {
    if (!riskGroups) {
      return [];
    }

    const mapGroup = (items: RiskGroupEntry[] | undefined): EquipmentLifespanData[] => {
      if (!items || items.length === 0) {
        return [];
      }

      return items
        .map((entry) => {
          const equipment = ('equipment' in entry ? entry.equipment : entry) as EquipmentSummary | undefined;
          if (!equipment) {
            return null;
          }

          const hasRequiredParams = hasManufacturerHealthParameters({
            manufacturerLifespan: equipment.manufacturerLifespan,
            commissionedDate: equipment.commissionedDate,
            installationDate: equipment.installationDate,
            startDate: equipment.startDate,
          });
          if (!hasRequiredParams) {
            return null;
          }

          const manufacturerLifespan = toPositiveNumber(equipment.manufacturerLifespan);
          const installationDate = equipment.commissionedDate ?? equipment.installationDate ?? equipment.startDate ?? null;
          const ageInYears = calculateAge(installationDate);
          if (manufacturerLifespan === null || ageInYears === null) {
            return null;
          }
          const isExpired = ageInYears >= manufacturerLifespan;

          return {
            id: equipment.id,
            equipmentDisplayId: equipment.displayId ?? '-',
            equipmentName: equipment.name ?? '-',
            categoryId: equipment.equipmentCategoryId || null,
            categoryInfo: equipment.categoryInfo || null,
            manufacturerLifespan,
            ageInYears,
            isExpired,
            installationDate,
          };
        })
        .filter((entry): entry is EquipmentLifespanData => entry !== null);
    };

    return [
      ...mapGroup(riskGroups?.critical),
      ...mapGroup(riskGroups?.high),
      ...mapGroup(riskGroups?.medium),
      ...mapGroup(riskGroups?.low),
    ];
  }, [riskGroups]);

  // フィルタリング
  let filteredData = equipmentData;
  if (selectedCategory) {
    filteredData = filteredData.filter(item => item.categoryId === selectedCategory);
  }
  if (selectedStatus === 'expired') {
    filteredData = filteredData.filter(item => item.isExpired);
  } else if (selectedStatus === 'active') {
    filteredData = filteredData.filter(item => !item.isExpired);
  }

  // 統計計算
  const expiredCount = equipmentData.filter(item => item.isExpired).length;
  const nearExpiryCount = equipmentData.filter(item => {
    const ratio = item.ageInYears / item.manufacturerLifespan;
    return ratio >= 0.8 && ratio < 1.0; // 耐用年数の80%~100%
  }).length;

  const columns: ColumnsType<EquipmentLifespanData> = [
    {
      title: '設備名',
      dataIndex: 'equipmentName',
      key: 'equipmentName',
      width: 250,
      render: (name: string, record: EquipmentLifespanData) => (
        <a
          href={`/app/equipments/${record.id}`}
          onClick={(e) => {
            e.preventDefault();
            navigate(`/app/equipments/${record.id}`);
          }}
          style={{
            color: '#1890ff',
            fontWeight: 'bold',
            textDecoration: 'none',
            cursor: 'pointer'
          }}
        >
          {name}
        </a>
      ),
      sorter: (a, b) => a.equipmentName.localeCompare(b.equipmentName),
    },
    {
      title: 'カテゴリ',
      dataIndex: 'categoryInfo',
      key: 'categoryInfo',
      width: 150,
      render: (categoryInfo: EquipmentCategoryInfo | null) => {
        if (!categoryInfo) return '-';
        return (
          <EquipmentCategoryDisplay
            category={categoryInfo}
            showIcon={true}
            noLink={true}
            size="small"
          />
        );
      },
    },
    {
      title: 'メーカー耐用年数',
      dataIndex: 'manufacturerLifespan',
      key: 'manufacturerLifespan',
      width: 140,
      render: (lifespan: number) => `${lifespan.toFixed(1)}年`,
      sorter: (a, b) => a.manufacturerLifespan - b.manufacturerLifespan,
    },
    {
      title: '経過年数',
      dataIndex: 'ageInYears',
      key: 'ageInYears',
      width: 120,
      render: (age: number) => `${age.toFixed(1)}年`,
      sorter: (a, b) => a.ageInYears - b.ageInYears,
    },
    {
      title: '稼働開始日',
      dataIndex: 'installationDate',
      key: 'installationDate',
      width: 120,
      render: (date: string | Date | null) => date ? new Date(date).toLocaleDateString('ja-JP') : '-',
    },
    {
      title: 'ステータス',
      dataIndex: 'isExpired',
      key: 'isExpired',
      width: 140,
      render: (isExpired: boolean, record: EquipmentLifespanData) => {
        if (isExpired) {
          return <Tag color="red" icon={<WarningOutlined />}>要交換</Tag>;
        }
        const ratio = record.ageInYears / record.manufacturerLifespan;
        if (ratio >= 0.8) {
          return <Tag color="orange">交換検討</Tag>;
        }
        return <Tag color="green" icon={<CheckCircleOutlined />}>良好</Tag>;
      },
      filters: [
        { text: '要交換', value: 'expired' },
        { text: '交換検討', value: 'near-expiry' },
        { text: '良好', value: 'active' },
      ],
      onFilter: (value, record) => {
        if (value === 'expired') return record.isExpired;
        if (value === 'near-expiry') {
          const ratio = record.ageInYears / record.manufacturerLifespan;
          return ratio >= 0.8 && ratio < 1.0;
        }
        if (value === 'active') {
          const ratio = record.ageInYears / record.manufacturerLifespan;
          return ratio < 0.8;
        }
        return true;
      },
    },
    {
      title: '残存年数',
      key: 'remainingYears',
      width: 120,
      render: (_: unknown, record: EquipmentLifespanData) => {
        const remaining = record.manufacturerLifespan - record.ageInYears;
        if (remaining <= 0) {
          return <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>超過</span>;
        }
        const color = remaining < 2 ? '#ff4d4f' : remaining < 5 ? '#faad14' : '#52c41a';
        return <span style={{ color }}>{remaining.toFixed(1)}年</span>;
      },
      sorter: (a, b) => {
        const remainingA = a.manufacturerLifespan - a.ageInYears;
        const remainingB = b.manufacturerLifespan - b.ageInYears;
        return remainingA - remainingB;
      },
    },
  ];

  return (
    <PageContainer>
      <PageHeader
        title="設備耐用年数管理"
        subtitle="メーカー指定の耐用年数を基準に、設備の交換時期を管理します"
      />

      <Alert
        message="耐用年数モード"
        description="詳細設定から「健全度モード」に変更すると、ワイブル分布による高度な健全度予測が利用できます。"
        type="info"
        showIcon
      />

      {expiredCount > 0 && (
        <Alert
          message={`耐用年数を超過した設備が${expiredCount}件あります`}
          description="メーカー耐用年数を超過している設備です。交換を検討してください。"
          type="error"
          icon={<WarningOutlined />}
          showIcon
          closable
        />
      )}

      {nearExpiryCount > 0 && (
        <Alert
          message={`耐用年数の80%を超えた設備が${nearExpiryCount}件あります`}
          description="近いうちに耐用年数に達する設備です。計画的な交換を検討してください。"
          type="warning"
          showIcon
          closable
        />
      )}

      <Row gutter={[16, 16]}>
        <Col xs={24} sm={8} md={6}>
          <Card>
            <Statistic
              title="総設備数"
              value={equipmentData.length}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8} md={6}>
          <Card
            style={{ cursor: 'pointer' }}
            onClick={() => setSelectedStatus('expired')}
          >
            <Statistic
              title="要交換（超過）"
              value={expiredCount}
              valueStyle={{ color: '#cf1322' }}
              prefix={<WarningOutlined />}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8} md={6}>
          <Card
            style={{ cursor: 'pointer' }}
            onClick={() => setSelectedStatus('all')}
          >
            <Statistic
              title="交換検討（80%超）"
              value={nearExpiryCount}
              valueStyle={{ color: '#faad14' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={8} md={6}>
          <Card>
            <Statistic
              title="良好"
              value={equipmentData.length - expiredCount - nearExpiryCount}
              valueStyle={{ color: '#52c41a' }}
              prefix={<CheckCircleOutlined />}
            />
          </Card>
        </Col>
      </Row>

      <Card>
        <Row gutter={[16, 16]}>
          <Col xs={24}>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <div>
                <Typography.Text strong style={{ marginBottom: 8, display: 'block' }}>カテゴリフィルタ</Typography.Text>
                <CategoryTagCloudFilter
                  categories={
                    categoriesData?.map(cat => ({
                      id: cat.id,
                      categoryLabel: cat.categoryLabel,
                      color: cat.color,
                      icon: cat.icon,
                      count: equipmentData.filter(e => e.categoryId === cat.id).length,
                    })) || []
                  }
                  selectedCategory={selectedCategory}
                  onCategoryChange={setSelectedCategory}
                  showCounts={true}
                  maxVisibleTags={15}
                />
              </div>
              <div>
                <Typography.Text strong style={{ marginBottom: 8, display: 'block' }}>ステータスフィルタ</Typography.Text>
                <Space wrap size={8}>
                  <Tag
                    color={selectedStatus === 'all' ? 'blue' : 'default'}
                    style={{ cursor: 'pointer', fontWeight: selectedStatus === 'all' ? 'bold' : 'normal' }}
                    onClick={() => setSelectedStatus('all')}
                  >
                    すべて
                  </Tag>
                  <Tag
                    color={selectedStatus === 'expired' ? 'red' : 'default'}
                    style={{ cursor: 'pointer', fontWeight: selectedStatus === 'expired' ? 'bold' : 'normal' }}
                    onClick={() => setSelectedStatus('expired')}
                  >
                    <WarningOutlined /> 要交換
                  </Tag>
                  <Tag
                    color={selectedStatus === 'active' ? 'green' : 'default'}
                    style={{ cursor: 'pointer', fontWeight: selectedStatus === 'active' ? 'bold' : 'normal' }}
                    onClick={() => setSelectedStatus('active')}
                  >
                    <CheckCircleOutlined /> 使用可能
                  </Tag>
                </Space>
              </div>
            </Space>
          </Col>
        </Row>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={filteredData}
          rowKey="id"
          loading={isLoading}
          scroll={{ x: 1000 }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            showSizeChanger: true,
            showTotal: (total) => `全 ${total} 件`,
            position: ['topRight', 'bottomRight'],
            onChange: (page, newPageSize) => {
              setCurrentPage(page);
              if (newPageSize && newPageSize !== pageSize) {
                setPageSize(newPageSize);
                setCurrentPage(1);
              }
            },
          }}
          rowClassName={(record) => {
            if (record.isExpired) return 'ant-table-row-expired';
            const ratio = record.ageInYears / record.manufacturerLifespan;
            if (ratio >= 0.8) return 'ant-table-row-warning';
            return '';
          }}
        />
      </Card>

      {error && (
        <Alert
          message="エラー"
          description="設備データの取得に失敗しました"
          type="error"
          showIcon
          style={{ marginTop: 16 }}
        />
      )}

      <style>{`
        .ant-table-row-expired {
          background-color: #fff1f0;
        }
        .ant-table-row-warning {
          background-color: #fff7e6;
        }
      `}</style>
    </PageContainer>
  );
}
